<script setup lang="ts">
import {computed, nextTick, onMounted, type PropType, ref, toRaw} from 'vue';
import {USER_CHANGE_EMIT, USER_REMOVE_EMIT} from '@/base/cabConstants';
import {CabOrganization, CabTheme, type CabUser, Role, uuidOf} from 'common';
import DeleteButton from '@/views/icons/DeleteIcon.vue';
import {CascaderOption, ElButton, ElCascader, ElDialog, ElMessage} from 'element-plus';
import {tempPassword} from '@/base/callBackend.js';

const emits = defineEmits([USER_REMOVE_EMIT, USER_CHANGE_EMIT]);
const props = defineProps({
    user: {
        type    : Object as PropType<CabUser>,
        required: true,
    },
    allOrganizations: {
        type    : Object as PropType<CabOrganization[]>,
        required: true,
    },
    selectableRoles: {
        type    : Object as PropType<Role[]>,
        required: false,
        default : Object.values(Role),
    },
    me: {
        type    : Object as PropType<CabUser>,
        required: true,
    },
    orgThemeOptions: {
        type    : Object as PropType<CascaderOption[]>,
        required: true,
    },
    allUsers: {
        type    : Object as PropType<CabUser[]>,
        required: true,
    },
});
defineExpose({
    firstEdit,
    refreshTempPassword,
});

const user               = ref<CabUser>(props.user);
const nameField          = ref<HTMLInputElement | null>(null);
const nameEditing        = ref<boolean>(false);
const emailField         = ref<HTMLInputElement | null>(null);
const hasTempPassword    = ref<boolean>(false);
const showPasswordDialog = ref<boolean>(false);
const tempPasswordValue  = ref<string>('');
const emailEditing       = ref<boolean>(false);
const orgThemeSelected   = ref(props.user?.getOrgThemeCombiUuids());
const roleOptions        = computed<{ label: string; value: string }[]>(() =>
    props.selectableRoles.map((role: Role) => ({
        label: role.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        value: role,
    })),
);

const isEmailDuplicate = computed<boolean>(() => {
    if (!user.value.email || !user.value.email.trim()) {
        return false;
    }
    const currentEmail = user.value.email.toLowerCase().trim();
    const duplicateCount = props.allUsers.filter(u => 
        u.email && u.email.toLowerCase().trim() === currentEmail
    ).length;
    return duplicateCount > 1;
});
let isFirstEdit: boolean = false;

onMounted(() => {
    checkTempPassword();
});

function refreshTempPassword() {
    checkTempPassword();
}

//==========================================
function firstEdit() {
    isFirstEdit = true;
    startEditingName();
}

//==========================================
function startEditingName() {
    nameEditing.value = true;
    nextTick(() => nameField.value!.focus());
}

function stopEditingName() {
    nameEditing.value = false;
    nameField.value!.blur();
    if (isFirstEdit) {
        if (!props.user.name.trim()) {
            onDelete();
        }
        isFirstEdit = false;
    }
}

//==========================================
function startEditingEmail() {
    emailEditing.value = true;
    nextTick(() => emailField.value!.focus());
}

function stopEditingEmail() {
    emailEditing.value = false;
    emailField.value!.blur();
}

//==========================================
function onDelete() {
    emits(USER_REMOVE_EMIT, user.value);
}

function onChangeOrgTheme() {
    const rawSelected: string[][] = toRaw(orgThemeSelected.value).map(path => toRaw(path));

    if (rawSelected.length === 0) {
        // Revert to previous selection
        orgThemeSelected.value = props.user.getOrgThemeCombiUuids();
        ElMessage.warning('Er moet minimaal één instelling/thema geselecteerd blijven.');
        return;
    }

    const newOrgs: CabOrganization[] = [];
    const newThemes: CabTheme[]      = [];
    rawSelected.forEach(([oUuid, tUuid]) => {
        const o = props.allOrganizations!.find((o) => uuidOf(o) === oUuid)!;
        const t = o.themes.find((t) => uuidOf(t) === tUuid)!;
        newOrgs.push(o);
        newThemes.push(t);
    });
    Object.assign(user.value, {
        organizations: newOrgs,
        themes       : newThemes,
    });
    onChange();
}

function onChange() {
    // Ensure roles is always a single-value array (if not empty)
    if (user.value.roles) {
        if (Array.isArray(user.value.roles)) {
            user.value.roles = user.value.roles.length > 0 ? [user.value.roles[0]] : [];
        } else if (typeof user.value.roles === 'string') {
            user.value.roles = [user.value.roles];
        }
    }
    emits(USER_CHANGE_EMIT, user.value);
}

function dynInputClassName(b: boolean) {
    return b ? `bg-whiteborder-gray-300 rounded-md shadow-xl` : `bg-yellow-100`;
}

function dynInputClassEmail(b: boolean) {
    //+ ' ' + (isEmailDuplicate ? 'bg-red-600 text-white' : '')
    return b ? `bg-whiteborder-gray-300 rounded-md shadow-xl ${isEmailDuplicate.value ? 'bg-red-600 text-white' : ''}`
        : `${isEmailDuplicate.value ? 'bg-red-600 text-white' : 'bg-yellow-100'}`;
}

//==========================================
// Temp password functionality
async function checkTempPassword() {
    hasTempPassword.value = false;
    if (user.value.email) {
        try {
            hasTempPassword.value = !!await tempPassword(user.value.email);
        } catch (error: any) {
            // leave it false
        }
    }
}

async function retrieveTempPassword() {
    if (user.value.email) {
        try {
            const pw                 = await tempPassword(user.value.email);
            tempPasswordValue.value  = pw || '';
            showPasswordDialog.value = true;
        } catch (error: any) {
            ElMessage.error('Fout bij ophalen tijdelijk wachtwoord');
        }
    }
}

function tempPasswordDialogClosed() {
    if (showPasswordDialog.value) {
        showPasswordDialog.value = false;
        tempPasswordValue.value  = '';
        checkTempPassword();
    }
}

// Watch for email changes to recheck temp password
function onEmailChange() {
    user.value.email = user.value.email ? user.value.email.toLowerCase() : '';
    onChange();
    checkTempPassword();
}
</script>

<template>
    <div
        class="flex flex-row items-center w-full font-medium m-0.5 p-1 text-gray-900 bg-yellow-100"
        data-testid="user-strip"
    >
        <div class="w-60 p-1" title="naam van de rapporteur">
            <input
                class="mt-1 mr-1 px-2 py-1 border-1 w-full"
                :class="dynInputClassName(nameEditing)"
                type="text"
                ref="nameField"
                v-model="user.name"
                @input="onChange"
                @click="startEditingName()"
                @focus="startEditingName()"
                @keyup.enter="stopEditingName()"
                @blur="stopEditingName()"
                placeholder="name"
                data-testid="user-name"
            />
        </div>

        <div class="w-60 p-1" title="email van de rapporteur">
            <input
                class="mt-1 mr-1 px-2 py-1 border-1 w-full"
                :class="dynInputClassEmail(emailEditing)"
                type="text"
                ref="emailField"
                v-model="user.email"
                @input="onEmailChange"
                @click="startEditingEmail()"
                @focus="startEditingEmail()"
                @keyup.enter="stopEditingEmail()"
                @blur="stopEditingEmail()"
                placeholder="email"
                data-testid="user-email"
            />
        </div>

        <div class="w-40 p-1" title="rol van de gebruiker">
            <el-cascader
                class="w-full"
                v-model="user.roles"
                :options="roleOptions"
                :disabled="roleOptions.length === 1"
                :props="{ checkStrictly: true, emitPath: false }"
                clearable
                placeholder="Selecteer rol"
                @change="onChange"
            />
        </div>

        <div class="text-xs text-gray-400">
            instelling/thema:
        </div>
        <div class="flex-1" data-testid="thema-org-select">
            <el-cascader
                class="w-full"
                v-model="orgThemeSelected"
                :options="orgThemeOptions"
                :props="{ multiple: true }"
                clearable
                placeholder="Selecteer instelling/themas"
                @change="onChangeOrgTheme"
            />
        </div>

        <div class="ml-2" v-if="hasTempPassword">
            <el-button
                type="primary"
                size="small"
                @click="retrieveTempPassword"
                title="Tijdelijk wachtwoord ophalen"
                :data-testid="`temp-pw-button`"
            >
                Tijdelijk WW
            </el-button>
        </div>

        <DeleteButton @click="onDelete()"/>
    </div>

    <el-dialog
        v-model="showPasswordDialog"
        :title="tempPasswordValue?`Tijdelijk wachtwoord voor ${user.email}`:'Tijdelijk wachtwoord niet meer beschikbaar.'"
        width="30%"
        @close="tempPasswordDialogClosed"
    >
        <div class="flex flex-col items-center" v-if="tempPasswordValue">
            <p class="font-mono font-extrabold text-2xl bg-gray-200 px-4 py-2 rounded select-all"
               data-testid="temp-pw">
                {{ tempPasswordValue }}
            </p>
        </div>
    </el-dialog>
</template>
